import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const About = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="about-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            About Us
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Simply Curated, <span className="italic font-normal">Thoughtfully Shared</span>
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            We believe skincare, makeup, and wellness shouldn't be overwhelming. Our mission is to cut through the noise with honest, calm, and practical recommendations.
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section data-testid="about-story" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C] mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-[#374151] leading-relaxed">
                <p>
                  SimplyCuratedGuide started from a simple frustration: finding honest product recommendations without the hype, pressure tactics, or sponsored bias that floods the beauty and wellness space.
                </p>
                <p>
                  We wanted a place where recommendations were based on real research, thoughtful ingredient analysis, and genuine user experiences—not flashy marketing or paid promotions.
                </p>
                <p>
                  Every guide we create is designed to help you make informed decisions. Whether you're building your first skincare routine, looking for minimal makeup essentials, or exploring wellness supplements, we're here to help—without the noise.
                </p>
              </div>
            </div>
            <div className="aspect-[4/5] bg-[#F2EBE3] overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_beauty-radar-1/artifacts/pq77ko9n_our-story.jpg.png" 
                alt="About SimplyCuratedGuide"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section data-testid="about-approach" className="py-20 md:py-32 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-12 md:mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C] mb-4">
              Our Approach
            </h2>
            <p className="text-[#374151]">
              We take a calm, research-informed approach to every recommendation we make.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-white border border-[#E5E5E5]">
              <span className="inline-block w-10 h-10 flex items-center justify-center bg-[#3A4D39] text-white font-heading text-lg mb-4">1</span>
              <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-3">Research First</h3>
              <p className="text-sm text-[#374151] leading-relaxed">
                We analyze ingredients, read clinical studies when available, and gather real user experiences before recommending any product.
              </p>
            </div>
            <div className="p-8 bg-white border border-[#E5E5E5]">
              <span className="inline-block w-10 h-10 flex items-center justify-center bg-[#3A4D39] text-white font-heading text-lg mb-4">2</span>
              <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-3">Honest Opinions</h3>
              <p className="text-sm text-[#374151] leading-relaxed">
                We don't accept payment for placement. Our affiliate relationships are clearly disclosed, and we only recommend what we genuinely believe in.
              </p>
            </div>
            <div className="p-8 bg-white border border-[#E5E5E5]">
              <span className="inline-block w-10 h-10 flex items-center justify-center bg-[#3A4D39] text-white font-heading text-lg mb-4">3</span>
              <h3 className="font-heading text-xl font-semibold text-[#1C1C1C] mb-3">No Pressure</h3>
              <p className="text-sm text-[#374151] leading-relaxed">
                Our guides educate first. We want you to understand why a product might work—not just that you should buy it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="about-cta" className="py-20 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1C1C1C] mb-6">
            Ready to explore?
          </h2>
          <p className="text-[#374151] mb-8">
            Start with our most popular guides or browse by category to find what you need.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/skincare"
              data-testid="about-cta-skincare"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29]"
            >
              Skincare Guides
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/makeup"
              data-testid="about-cta-makeup"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#3A4D39] text-[#3A4D39] text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#3A4D39] hover:text-white"
            >
              Makeup Guides
            </Link>
            <Link 
              to="/wellness"
              data-testid="about-cta-wellness"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#3A4D39] text-[#3A4D39] text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#3A4D39] hover:text-white"
            >
              Wellness Guides
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
